import { fetchWithTimeout, BULK_FETCH_TIMEOUT, parseErrorResponse } from './base.ts';
import type { DocxLayout } from '../../components/reports/docxLayout.ts';

export interface GeneratedReport {
  id: string;
  templateId: string;
  personId: string | null;
  personName: string | null;
  config: Record<string, unknown>;
  entryCount: number;
  generatedAt: string;
  pdfPath: string | null;
}

/** Een document of sjabloon uit de rapportbouwer (#1300). */
export interface ReportDocumentRecord {
  id: string;
  kind: 'document' | 'template' | 'style';
  title: string;
  blocks: unknown[];
  style: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ReportDocumentInput {
  kind?: 'document' | 'template' | 'style';
  title?: string;
  blocks?: unknown[];
  style?: Record<string, unknown>;
}

/**
 * #1582 — een sjabloon of stijlset op account- of organisatieniveau. De stijl
 * staat er niet volledig in: `presetId` + alleen de afwijkingen (`overrides`).
 * `presetId: null` = opslag van vóór #1582; dan is `overrides` de hele stijl.
 * Samenvoegen: `stijlUitSjabloon` in `components/reports/reportTemplateInheritance.ts`.
 */
export interface ReportTemplateRecord {
  id: string;
  kind: 'template' | 'style';
  title: string;
  blocks: unknown[];
  presetId: string | null;
  presetVersion: number | null;
  overrides: Record<string, unknown>;
  /** Gevuld = gedeeld met deze organisatie. */
  organizationId: string | null;
  organizationName: string | null;
  isOwner: boolean;
  /** Eigen sjabloon, of gedeeld en jij bent owner/admin van de organisatie. */
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReportTemplateInput {
  kind?: 'template' | 'style';
  title?: string;
  blocks?: unknown[];
  presetId?: string | null;
  presetVersion?: number | null;
  overrides?: Record<string, unknown>;
}

export interface PageSetup {
  orientation?: 'portrait' | 'landscape';
  pageSize?: string;
  margins?: { top: number; right: number; bottom: number; left: number };
}

export interface RunningMarginals {
  headerHtml?: string;
  footerHtml?: string;
}

/**
 * Taal van het document (`nl-NL`, `en-GB`, …). Zonder deze markering staat er
 * `en-US` in het Word-bestand en onderstreept de tekstverwerker elk Nederlands
 * woord als spelfout — inclusief elke naam en plaats in de kwartierstaat.
 */
export type DocumentLanguage = { lang?: string };

/** #1587 — de Word-opmaak (stijlen, kop en voet per hoofdstuk) uit `docxLayout.ts`. */
export type DocxExportOptions = { layout?: DocxLayout } & DocumentLanguage;

/**
 * Lopende kop- en voetregel voor de PDF.
 *
 * Puppeteer rendert deze in een eigen context: geen paginastijl, geen JS, alleen
 * inline styles. `<span class="pageNumber">` en `<span class="totalPages">` vult
 * de renderer zelf in — dat is de enige manier om een echt paginanummer op elke
 * pagina te krijgen.
 */
export type PdfExportOptions = PageSetup &
  RunningMarginals &
  DocumentLanguage & {
    /**
     * #1581 — papier, marges, kop en voet staan als @page-regels in de CSS van het
     * document; de server drukt af in twee rondes voor paginanummers in inhoud en index.
     */
    pagedMedia?: boolean;
  };

export const reportsApi = {
  getReportHistory: async (projectId: string): Promise<GeneratedReport[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/reports/history`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  saveReport: async (
    projectId: string,
    data: {
      templateId: string;
      personId: string;
      personName: string;
      config: Record<string, unknown>;
      entryCount: number;
    }
  ): Promise<GeneratedReport> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Export a rendered HTML report to PDF via server-side Puppeteer.
   * Returns a Blob containing the PDF data.
   */
  exportReportPdf: async (
    projectId: string,
    html: string,
    title: string,
    options: PdfExportOptions = {}
  ): Promise<Blob> => {
    const res = await fetchWithTimeout(
      '/api/reports/export-pdf',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html, title, projectId, ...options }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.blob();
  },
  // ── Rapportbouwer (#1300) ─────────────────────────────────────────────────

  listReportDocuments: async (
    projectId: string,
    kind?: 'document' | 'template' | 'style'
  ): Promise<ReportDocumentRecord[]> => {
    const qs = kind ? `?kind=${kind}` : '';
    const res = await fetchWithTimeout(`/api/projects/${projectId}/report-documents${qs}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getReportDocument: async (projectId: string, id: string): Promise<ReportDocumentRecord> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/report-documents/${id}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  createReportDocument: async (
    projectId: string,
    data: ReportDocumentInput
  ): Promise<ReportDocumentRecord> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/report-documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  updateReportDocument: async (
    projectId: string,
    id: string,
    data: ReportDocumentInput
  ): Promise<ReportDocumentRecord> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/report-documents/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteReportDocument: async (projectId: string, id: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/report-documents/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  // ── Sjablonen op account- en organisatieniveau (#1582) ───────────────────

  listReportTemplates: async (kind?: 'template' | 'style'): Promise<ReportTemplateRecord[]> => {
    const res = await fetchWithTimeout(`/api/report-templates${kind ? `?kind=${kind}` : ''}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  createReportTemplate: async (data: ReportTemplateInput): Promise<ReportTemplateRecord> => {
    const res = await fetchWithTimeout('/api/report-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  updateReportTemplate: async (
    id: string,
    data: ReportTemplateInput
  ): Promise<ReportTemplateRecord> => {
    const res = await fetchWithTimeout(`/api/report-templates/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteReportTemplate: async (id: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/report-templates/${id}`, { method: 'DELETE' });
    if (!res.ok) await parseErrorResponse(res);
  },

  /** `organizationId: null` haalt het terug naar de maker. Alleen org-owner/-admin. */
  shareReportTemplate: async (id: string, organizationId: string | null): Promise<void> => {
    const res = await fetchWithTimeout(`/api/report-templates/${id}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId }),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  /**
   * Export een gerenderd rapport naar Word. Zelfde HTML als de PDF-export —
   * daar staat de reden bij waarom die HTML semantisch moet zijn.
   */
  exportReportDocx: async (
    projectId: string,
    html: string,
    title: string,
    options: DocxExportOptions = {}
  ): Promise<Blob> => {
    const res = await fetchWithTimeout(
      '/api/reports/export-docx',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html, title, projectId, ...options }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.blob();
  },
  /**
   * Opent het rapport in Collabora Online om het af te ronden.
   *
   * De HTML gaat mee vanaf de client: alleen de browser weet hoe de blokken met
   * de gekozen opmaak renderen. Wat de gebruiker daar opslaat komt via de
   * WOPI-callback terug als `collaboraHtml` op het document — de blokstructuur
   * overleeft die reis niet, dus hij overschrijft de blokken niet.
   */
  createReportCollaboraSession: async (
    projectId: string,
    documentId: string,
    html: string,
    lang?: string,
    layout?: DocxLayout
  ): Promise<{ formAction: string; accessToken: string; accessTokenTtl: number }> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/report-documents/${documentId}/collabora-session`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html, lang, layout }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
